import { Component, OnInit, OnDestroy, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { Logger } from '../@shared/logger.service';
import { CredentialsService } from './credentials.service';

const log = new Logger('LoginComponent');

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  public userName: string | undefined;
  public userEmail: string | undefined;
  public loggedIn = false;
  public authState: string = 'loading';
  private hubListenerCancel?: () => void;

  constructor(public router: Router, public credentialsService: CredentialsService, private ngZone: NgZone) {}

  ngOnInit() {
    log.debug('LoginComponent initialized');

    // Clear any existing authentication state first
    this.clearAuthState();

    // Check auth state after a brief delay to ensure Amplify is fully initialized
    setTimeout(() => {
      this.checkAuthState();
    }, 100);

    this.setupAuthListener();
  }

  ngOnDestroy() {
    if (this.hubListenerCancel) {
      this.hubListenerCancel();
    }
  }

  private clearAuthState() {
    log.debug('Clearing authentication state');
    this.loggedIn = false;
    this.authState = 'loading';
    this.userName = undefined;
    this.userEmail = undefined;
  }

  private async checkAuthState() {
    try {
      log.debug('Checking authentication state...');

      // First check if we have a current session
      const [session, user, attributes] = await Promise.all([
        fetchAuthSession(),
        getCurrentUser(),
        fetchUserAttributes(),
      ]);
      log.debug('Current session found, checking if valid');

      if (session.tokens?.accessToken) {
        // Session is valid, get user details
        log.debug('User is authenticated with valid session:', user.username);

        this.ngZone.run(() => {
          this.loggedIn = true;
          this.authState = 'signedIn';
          this.userName = attributes.name || user.username;
          this.userEmail = attributes.email;
        });

        // Redirect if already authenticated
        this.router.navigate(['/home']);
      } else {
        throw new Error('No valid access token is available');
      }
    } catch (error) {
      log.debug('User is not authenticated or session expired:', error);

      this.ngZone.run(() => {
        this.loggedIn = false;
        this.authState = 'signIn';
      });
    }
  }

  private setupAuthListener() {
    this.hubListenerCancel = Hub.listen('auth', ({ payload }) => {
      const event = payload.event;
      log.debug('Auth event received:', event);

      this.ngZone.run(() => {
        switch (event) {
          case 'signedIn':
            log.debug('User signed in successfully');
            void this.checkAuthState();
            break;

          case 'signedOut':
            log.debug('User signed out');
            this.clearAuthState();
            this.authState = 'signIn';
            break;

          case 'tokenRefresh_failure':
            log.debug('Token refresh failed - session expired');
            this.clearAuthState();
            this.authState = 'signIn';
            break;

          case 'signInWithRedirect_failure':
            log.debug('Sign in failed');
            this.authState = 'signIn';
            break;
        }
      });
    });
  }
}
